import colors from '../../../styles/constants.module.scss'

const escapeHtml = (str: string): string => {
  const div = document.createElement('div')
  div.textContent = str
  return div.innerHTML
}

interface PrintTemplateParams {
  title: string
  headerImageSrc: string
  headerImageAlt: string
  timestamp?: string
  timestampLabel?: string
  tableHeaders: string[]
  tableRows: string
}

const getPrintStyles = (): string => {
  return `
    @page {
      margin: 0.5in;
      size: A4;
    }
    
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
      font-size: 0.75rem;
      color: ${colors.neutral90};
      padding: 0;
      margin: 0;
    }
    
    .header {
      width: 100%;
      margin-bottom: 1rem;
    }
    
    .header img {
      width: 100%;
      height: auto;
      display: block;
    }
    
    .timestamp {
      color: ${colors.neutral70};
      font-size: 0.875rem;
      margin-bottom: 1rem;
      font-style: italic;
    }
    
    table {
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
      margin-top: 1rem;
    }
    
    thead {
      background-color: ${colors.neutral20Background};
    }
    
    th {
      font-weight: 700;
      color: ${colors.primary60Hover};
      border: 1px solid ${colors.neutral30};
      border-bottom: 2px solid ${colors.secondary50};
      padding: 0.5rem;
      text-align: left;
      text-transform: uppercase;
      font-size: 0.75rem;
      letter-spacing: 0.5px;
    }
    
    td {
      padding: 0.5rem;
      border: 1px solid ${colors.neutral30};
      vertical-align: top;
      color: ${colors.neutral90};
      font-size: 0.75rem;
    }
    
    tr:nth-child(even) {
      background-color: ${colors.neutral10Background};
    }
    
    th:nth-child(1), td:nth-child(1) { width: 18%; }
    th:nth-child(2), td:nth-child(2) { width: 44%; }
    th:nth-child(3), td:nth-child(3) { width: 19%; }
    th:nth-child(4), td:nth-child(4) { width: 19%; }
    
    @media print {
      body {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      
      table {
        page-break-inside: auto;
      }
      
      tr {
        page-break-inside: avoid;
        page-break-after: auto;
      }
      
      thead {
        display: table-header-group;
      }
      
      tbody {
        display: table-row-group;
      }
    }
  `
}

export const generatePrintTemplate = (params: PrintTemplateParams): string => {
  const { title, headerImageSrc, headerImageAlt, timestamp, timestampLabel, tableHeaders, tableRows } = params

  return `
    <!DOCTYPE html>
    <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${escapeHtml(title)}</title>
        <style>${getPrintStyles()}</style>
      </head>
      <body>
        <div class="header">
          <img src="${escapeHtml(headerImageSrc)}" alt="${escapeHtml(headerImageAlt)}" />
        </div>
        ${
          timestamp && timestampLabel
            ? `<div class="timestamp"><strong>${escapeHtml(timestampLabel)}:</strong> ${escapeHtml(timestamp)}</div>`
            : ''
        }
        <table>
          <thead>
            <tr>
              ${tableHeaders.map((header) => `<th>${escapeHtml(header)}</th>`).join('\n              ')}
            </tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>
      </body>
    </html>
  `
}

export { escapeHtml }
